/**
 * 🏢 Corporate Professional Theme
 * Tema corporativo con paleta conservadora y máxima profesionalidad
 * 
 * @description Diseño empresarial con colores neutros, excelente legibilidad
 * y apariencia formal perfecta para entornos corporativos
 */

export const corporateTheme = {
  name: 'Corporate Professional',
  mode: 'light',
  
  // 🎨 Paleta corporativa conservadora
  palette: {
    primary: {
      main: '#2563EB',        // Azul corporativo confiable
      light: '#60A5FA',       // Azul claro profesional
      dark: '#1D4ED8',        // Azul oscuro serio
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#64748B',        // Gris azulado neutral
      light: '#94A3B8',       // Gris claro
      dark: '#475569',        // Gris oscuro
      contrastText: '#FFFFFF'
    },
    error: {
      main: '#DC2626',
      light: '#F87171',
      dark: '#B91C1C'
    },
    warning: {
      main: '#D97706',
      light: '#FBBF24',
      dark: '#B45309'
    },
    info: {
      main: '#0284C7',
      light: '#0EA5E9',
      dark: '#0369A1'
    },
    success: {
      main: '#059669',
      light: '#10B981',
      dark: '#047857'
    },
    
    // 🖼️ Fondos corporativos limpios
    background: {
      default: '#F8FAFC',      // Fondo principal muy limpio
      paper: '#FFFFFF',        // Tarjetas blancas puras
      elevated: '#F1F5F9'      // Elementos elevados sutiles
    },
    
    // 📝 Textos corporativos legibles
    text: {
      primary: '#0F172A',      // Negro corporativo
      secondary: '#475569',    // Gris profesional
      disabled: '#94A3B8',     // Gris claro para deshabilitados
      hint: '#CBD5E1'         // Gris muy claro para hints
    },
    
    // 🎯 Colores de acción corporativos
    action: {
      active: '#2563EB',
      hover: 'rgba(37, 99, 235, 0.04)',
      selected: 'rgba(37, 99, 235, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)'
    },
    
    // 📏 Divisores sutiles
    divider: 'rgba(0, 0, 0, 0.06)',

    // 🦅 Colores de marca AI Flock
    brand: {
      main: '#9333EA',          // AI Flock purple principal
      light: '#A855F7',         // Purple claro
      dark: '#7C3AED',          // Purple oscuro
      contrastText: '#FFFFFF'
    }
  },

  // ✨ Efectos visuales (shadows, overlays, hover states) - Corporate
  effects: {
    // Sombras conservadoras para diseño profesional
    shadows: {
      subtle: '0px 1px 2px rgba(0, 0, 0, 0.05)',
      medium: '0px 2px 4px rgba(0, 0, 0, 0.08)',
      strong: '0px 4px 16px rgba(0, 0, 0, 0.12)',
      brand: '0 4px 16px rgba(37, 99, 235, 0.15)',      // Sombra azul corporativo
      brandMedium: '0 2px 6px rgba(37, 99, 235, 0.08)',
      brandStrong: '0 4px 12px rgba(37, 99, 235, 0.12)'
    },

    // Estados hover discretos
    hover: {
      background: 'rgba(0, 0, 0, 0.03)',                // Hover muy sutil
      primary: 'rgba(37, 99, 235, 0.06)',               // Hover con color primario
      card: '0px 1px 3px rgba(0, 0, 0, 0.08)',          // Sombra en hover de cards
      cardBackground: 'rgba(37, 99, 235, 0.03)'         // Background hover de cards
    },

    // Overlays profesionales
    overlay: {
      loading: 'rgba(0, 0, 0, 0.65)',                   // Overlay loading
      modal: 'rgba(0, 0, 0, 0.45)',                     // Overlay modales
      subtle: 'rgba(0, 0, 0, 0.25)'                     // Overlay sutil
    },

    // Estados de advertencia corporativos
    warning: {
      background: 'rgba(234, 179, 8, 0.12)',            // Background warning (amarillo/dorado)
      hover: 'rgba(234, 179, 8, 0.18)',                 // Hover warning
      border: 'rgba(234, 179, 8, 0.25)'                 // Border warning
    }
  },

  // 🎛️ Configuración específica por sección
  sections: {
    // 💬 Chat Interface
    chat: {
      background: '#F8FAFC',
      messageBg: '#FFFFFF',
      inputBg: '#F1F5F9',
      userMessageBg: '#EFF6FF',
      botMessageBg: '#FFFFFF',
      border: '#E2E8F0',
      shadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
    },

    // 🎨 Flow Editor - DISEÑO CORPORATIVO LIMPIO
    editor: {
      canvasBg: '#F8FAFC',          // Fondo corporativo limpio
      nodeBg: '#FFFFFF',            // Nodos blancos profesionales
      nodeBorder: '#E2E8F0',        // Bordes sutiles
      nodeSelected: '#2563EB',      // Azul corporativo para selección
      nodeHover: '#F1F5F9',         // Hover muy sutil
      toolbarBg: 'rgba(255, 255, 255, 0.98)', // Toolbar casi opaco
      toolbarShadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
      connectionDefault: '#94A3B8',  // Conexiones grises neutras
      connectionActive: '#2563EB',   // Conexiones activas azul corporativo
      miniMapBg: 'rgba(241, 245, 249, 0.95)',
      gridColor: '#D6DDE6'          // ~1,31:1 sobre canvasBg; frío, acompaña al azul corporativo
    },

    // 👥 Roles Editor
    roles: {
      cardBg: '#FFFFFF',
      sidebarBg: '#F8FAFC',
      accent: '#2563EB',
      border: '#E2E8F0',
      hover: '#F1F5F9',
      selected: '#EFF6FF'
    },

    // ⚙️ Settings
    settings: {
      panelBg: '#F8FAFC',
      cardBg: '#FFFFFF',
      border: '#E2E8F0',
      accent: '#2563EB'
    },

    // 🎯 Onboarding
    onboarding: {
      bg: '#F8FAFC',
      cardBg: '#FFFFFF',
      accent: '#2563EB',
      progress: '#60A5FA'
    }
  },

  // 🎭 Tipografía corporativa seria
  typography: {
    fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    
    h1: { fontSize: '2.25rem', fontWeight: 700, color: '#0F172A', lineHeight: 1.2 },
    h2: { fontSize: '1.875rem', fontWeight: 700, color: '#0F172A', lineHeight: 1.3 },
    h3: { fontSize: '1.5rem', fontWeight: 600, color: '#0F172A', lineHeight: 1.4 },
    h4: { fontSize: '1.25rem', fontWeight: 600, color: '#1E293B', lineHeight: 1.4 },
    h5: { fontSize: '1.125rem', fontWeight: 600, color: '#1E293B', lineHeight: 1.5 },
    h6: { fontSize: '1rem', fontWeight: 600, color: '#334155', lineHeight: 1.5 },
    
    body1: { fontSize: '1rem', color: '#334155', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', color: '#475569', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', color: '#64748B', lineHeight: 1.5 },
    button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.025em' }
  },

  // ✨ Sombras corporativas conservadoras
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.04)',
    md: '0 2px 4px rgba(0, 0, 0, 0.06)',
    lg: '0 4px 8px rgba(0, 0, 0, 0.08)',
    xl: '0 8px 16px rgba(0, 0, 0, 0.1)',
    
    // Sombras específicas para componentes
    card: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
    modal: '0 20px 40px rgba(0, 0, 0, 0.15)',
    tooltip: '0 2px 4px rgba(0, 0, 0, 0.08)'
  },

  // 📏 Espaciado corporativo preciso
  spacing: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    xxl: '3rem'       // 48px
  },

  // 🎭 Animaciones corporativas sutiles
  animations: {
    fast: '120ms ease-out',
    normal: '200ms ease-out',
    slow: '300ms ease-out',
    easing: 'ease-out',
    
    // Animaciones específicas más conservadoras
    fadeIn: 'fadeIn 150ms ease-out',
    slideUp: 'slideUp 200ms ease-out',
    bounce: 'none' // Sin bounce para look corporativo
  },

  // 📱 Breakpoints responsivos
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920
  },

  // 🎨 Overrides de componentes MUI corporativos
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F8FAFC',
          color: '#334155',
          fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
        }
      }
    },
    
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          borderRadius: 6, // Bordes menos redondeados
          border: '1px solid #E2E8F0'
        }
      }
    },
    
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6, // Menos redondeado
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: '0.025em',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }
        },
        contained: {
          background: '#2563EB',
          '&:hover': {
            background: '#1D4ED8'
          }
        }
      }
    },
    
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid #E2E8F0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
        }
      }
    }
  }
};
